import { useParams, useNavigate } from "react-router-dom";
import { Form, Input, message, Select } from "antd";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import { invalidateCatalog } from "../../../hooks/useCatalogData";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { fetchExchangeRates, EXCHANGE_RATES_UPDATED_EVENT } from "../../../shared/lib/exchangeRates";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { FormPage, FormCard, inputStyle, grid2, T, cardStyle } from "../../../shared/ui/PageShell";

const IncreasePayment = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [amountSingle, setAmountSingle] = useState("");
  const [amount_main, setAmount_main] = useState("");
  const [amount_secondary, setAmount_secondary] = useState("");
  const [exchangeRateId, setExchangeRateId] = useState(null);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [exchangeRates, setExchangeRates] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const useDualCurrency = Boolean(systemConfig?.secondary_currency_code);
  const mainCode = systemConfig?.currency_code || "IQD";
  const secCode = systemConfig?.secondary_currency_code || "";

  const selectedRate = useMemo(() => {
    if (!exchangeRateId || !exchangeRates.length) return null;
    return exchangeRates.find((r) => Number(r.id) === Number(exchangeRateId)) || null;
  }, [exchangeRateId, exchangeRates]);

  const appliedMainImpact = useMemo(() => {
    const main = toApiNumber(amount_main, 0);
    const sec = toApiNumber(amount_secondary, 0);
    if (!useDualCurrency) return Math.abs(toApiNumber(amountSingle, 0));
    const secondaryPerMain = selectedRate ? toApiNumber(selectedRate.main_per_secondary, 0) : 0;
    if (!secondaryPerMain) return Math.round(main * 100) / 100;
    return Math.round((main + sec / secondaryPerMain) * 100) / 100;
  }, [amount_main, amount_secondary, amountSingle, selectedRate, useDualCurrency]);

  const loadFx = useCallback(async () => {
    try {
      const cfg = await fetchSystemConfig();
      setSystemConfig(cfg);
      if (!cfg?.secondary_currency_code) {
        setExchangeRates([]);
        setExchangeRateId(null);
        return;
      }
      const rates = await fetchExchangeRates();
      setExchangeRates(rates);
      setExchangeRateId((prev) =>
        prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
      );
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadFx(); }, [loadFx]);

  useEffect(() => {
    const onFxUpdated = () => loadFx();
    window.addEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
    return () => window.removeEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
  }, [loadFx]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/customers/${id}`);
        if (cancelled) return;
        setCustomerInfo({
          name: res?.data?.name || "",
          total_owed: toApiNumber(res?.data?.total_owed, 0),
        });
      } catch {
        if (!cancelled) setCustomerInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const onSubmit = async () => {
    const secVal = Math.round(toApiNumber(amount_secondary, 0) * 100) / 100;
    if (useDualCurrency && secVal > 0 && !exchangeRateId) {
      message.error(t("increaseDebtRateRequired", "Select a rate when secondary currency is used."));
      return;
    }
    if (useDualCurrency) {
      const m = toApiNumber(amount_main, 0);
      if (m <= 0 && secVal <= 0) { message.error(t("increaseDebtEnterAmount", "Enter an amount greater than zero.")); return; }
    } else {
      if (toApiNumber(amountSingle, 0) <= 0) { message.error(t("increaseDebtEnterAmount", "Enter an amount greater than zero.")); return; }
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("increase_debt", "1");
      fd.append("payment_method", "adjustment");
      if (useDualCurrency) {
        fd.append("amount_main", String(Math.round(toApiNumber(amount_main, 0) * 100) / 100));
        fd.append("amount_secondary", String(secVal));
        if (secVal > 0 && exchangeRateId) {
          fd.append("exchange_rate_id", String(exchangeRateId));
        }
      } else {
        fd.append("amount", String(Math.abs(toApiNumber(amountSingle, 0))));
      }
      await apiClient.post(`/customers/pay/${id}`, fd);
      invalidateCatalog("customers");
      message.success(t("increaseDebtSuccess", "Debt increase recorded."));
      navigate("/customers");
    } catch (error) {
      message.error(error?.response?.data?.message || t("increaseDebtError", "Recording failed."));
    } finally {
      setSubmitting(false);
    }
  };

  const sidePanel = (
    <div style={cardStyle}>
      <div style={{ padding: "16px 18px" }}>
        <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("customer", "Customer")}</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginTop: 3 }}>{customerInfo?.name || "—"}</div>

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.borderL}` }}>
          <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("totalOwed", "Total Owed")}</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: T.red, marginTop: 3 }}>
            {formatCurrency(customerInfo?.total_owed || 0, mainCode)}
          </div>
        </div>

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.borderL}` }}>
          <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t("increaseDebtTotalMainImpact", "Total main currency added to debt")}</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#d97706", marginTop: 3 }}>
            + {formatCurrency(appliedMainImpact, mainCode)}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <FormPage
      breadcrumb={[t("sidebarSectionContacts", "Contacts"), t("customers", "Customers"), t("increaseDebtTitle", "Increase Customer Debt")]}
      title={t("increaseDebtTitle", "Increase Customer Debt")}
      subtitle={customerInfo?.name || t("increaseDebtSubtitle", "Recorded as a negative payment (adjustment) equal to the debt added. With dual currency, the main currency total is counted as a payment.")}
      onBack={() => navigate("/customers")}
      onSave={onSubmit}
      saving={submitting}
      sidePanel={sidePanel}
    >
      <Form layout="vertical">
        <FormCard title={t("increaseDebtTitle", "Increase Customer Debt")} subtitle={t("increaseDebtSubtitle", "Recorded as a negative payment (adjustment) equal to the debt added. With dual currency, the main currency total is counted as a payment.")}>
          {useDualCurrency ? (
            <>
              <div style={grid2}>
                <Form.Item label={t("increaseDebtAmountMain", "Amount ({{code}})", { code: mainCode })}>
                  <Input
                    type="number" min={0} step="any"
                    value={amount_main}
                    onChange={(e) => setAmount_main(e.target.value)}
                    placeholder="0"
                    style={inputStyle}
                  />
                </Form.Item>
                <Form.Item label={t("increaseDebtAmountSecondary", "Amount ({{code}})", { code: secCode })}>
                  <Input
                    type="number" min={0} step="any"
                    value={amount_secondary}
                    onChange={(e) => setAmount_secondary(e.target.value)}
                    placeholder="0"
                    style={inputStyle}
                  />
                </Form.Item>
              </div>
              {exchangeRates.length > 0 && (
                <Form.Item label={t("rateLabel", "Rate")}>
                  <Select
                    style={{ ...inputStyle, height: undefined, width: "100%" }}
                    placeholder={t("increaseDebtSelectRate", "Select exchange rate")}
                    value={exchangeRateId}
                    onChange={setExchangeRateId}
                    options={exchangeRates.map((r) => ({
                      value: r.id,
                      label: `${String(r.effective_from || "").slice(0, 10)} — 1 ${mainCode} = ${r.main_per_secondary} ${secCode}`,
                    }))}
                  />
                </Form.Item>
              )}
            </>
          ) : (
            <Form.Item label={t("increaseDebtSingleAmount", "Amount ({{code}})", { code: mainCode })}>
              <Input
                type="number" min={0} step="any"
                value={amountSingle}
                onChange={(e) => setAmountSingle(e.target.value)}
                placeholder="0"
                style={inputStyle}
              />
            </Form.Item>
          )}
        </FormCard>
      </Form>
    </FormPage>
  );
};

export default IncreasePayment;
